import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { analyticsServicesService } from "@/services/analytics-services-service";
import { ApiError } from "@/services/BaseService";
import { useHotelService } from "@/hooks/useHotelServices";
import type { DateRange } from "@/hooks/useDateRange";
import type { TopServiceRow } from "../components/services/mockData";
import {
  mapDiscountImpactToChart,
  mapHotelServiceToDetailExtras,
  mapNavigationServiceToExtras,
  mapPopularityToMetrics,
  mapServiceReviewsToDetailReviews,
  mapUsageByDayToChart,
} from "../components/services/serviceDetailMappers";
import {
  type ServiceDetailAnalytics,
  type ServiceDetailExtras,
} from "../components/services/serviceDetailTypes";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export const analyticsServiceDetailKeys = {
  all: ["analytics-service-detail"] as const,
  popularity: (serviceId: string, hotelId: string, from: string, to: string) =>
    [
      ...analyticsServiceDetailKeys.all,
      "popularity",
      serviceId,
      hotelId,
      from,
      to,
    ] as const,
  usageByDay: (serviceId: string, hotelId: string, from: string, to: string) =>
    [
      ...analyticsServiceDetailKeys.all,
      "usage-by-day",
      serviceId,
      hotelId,
      from,
      to,
    ] as const,
  discountImpact: (
    serviceId: string,
    hotelId: string,
    from: string,
    to: string,
  ) =>
    [
      ...analyticsServiceDetailKeys.all,
      "discount-impact",
      serviceId,
      hotelId,
      from,
      to,
    ] as const,
  reviews: (serviceId: string, hotelId: string, from: string, to: string) =>
    [
      ...analyticsServiceDetailKeys.all,
      "reviews",
      serviceId,
      hotelId,
      from,
      to,
    ] as const,
};

export function useAnalyticsServiceDetail(
  serviceId: string | undefined,
  dateRange: DateRange,
  navigationService?: TopServiceRow,
) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;

  const rangeQuery = useMemo(
    () => ({ hotelId, from, to }),
    [from, hotelId, to],
  );

  const shouldFetchHotelService =
    isReady && !!serviceId && navigationService?.id !== serviceId;

  const hotelServiceQuery = useHotelService(
    shouldFetchHotelService ? (serviceId ?? "") : "",
    shouldFetchHotelService ? hotelId : "",
  );

  const popularity = useQuery({
    queryKey: analyticsServiceDetailKeys.popularity(
      serviceId ?? "",
      hotelId,
      from,
      to,
    ),
    queryFn: () =>
      analyticsServicesService.getPopularity(serviceId!, rangeQuery),
    enabled: isReady && !!serviceId,
  });

  const usageByDay = useQuery({
    queryKey: analyticsServiceDetailKeys.usageByDay(
      serviceId ?? "",
      hotelId,
      from,
      to,
    ),
    queryFn: () =>
      analyticsServicesService.getUsageByDay(serviceId!, rangeQuery),
    enabled: isReady && !!serviceId,
  });

  const discountImpact = useQuery({
    queryKey: analyticsServiceDetailKeys.discountImpact(
      serviceId ?? "",
      hotelId,
      from,
      to,
    ),
    queryFn: () =>
      analyticsServicesService.getDiscountImpact(serviceId!, rangeQuery),
    enabled: isReady && !!serviceId,
  });

  const reviews = useQuery({
    queryKey: analyticsServiceDetailKeys.reviews(
      serviceId ?? "",
      hotelId,
      from,
      to,
    ),
    queryFn: () => analyticsServicesService.getReviews(serviceId!, rangeQuery),
    enabled: isReady && !!serviceId,
  });

  const serviceName =
    navigationService?.name ??
    hotelServiceQuery.data?.name ??
    discountImpact.data?.service.name;

  const extras: ServiceDetailExtras | null = useMemo(() => {
    if (navigationService && navigationService.id === serviceId) {
      return mapNavigationServiceToExtras(navigationService);
    }
    if (hotelServiceQuery.data) {
      return mapHotelServiceToDetailExtras(hotelServiceQuery.data);
    }
    return null;
  }, [hotelServiceQuery.data, navigationService, serviceId]);

  const analytics: ServiceDetailAnalytics | null = useMemo(() => {
    if (
      !popularity.data ||
      !usageByDay.data ||
      !discountImpact.data ||
      !reviews.data
    ) {
      return null;
    }

    return {
      popularity: mapPopularityToMetrics(popularity.data),
      ...mapUsageByDayToChart(usageByDay.data),
      ...mapDiscountImpactToChart(discountImpact.data),
      ...mapServiceReviewsToDetailReviews(reviews.data),
    };
  }, [discountImpact.data, popularity.data, reviews.data, usageByDay.data]);

  const isNotFound =
    popularity.error instanceof ApiError && popularity.error.statusCode === 404;

  const isLoading =
    !isReady ||
    popularity.isLoading ||
    usageByDay.isLoading ||
    discountImpact.isLoading ||
    reviews.isLoading ||
    (!extras && hotelServiceQuery.isLoading && !navigationService);

  const isError =
    !isNotFound &&
    (popularity.isError ||
      usageByDay.isError ||
      discountImpact.isError ||
      reviews.isError);

  return {
    ...rangeParams,
    serviceName,
    extras,
    analytics,
    popularity,
    usageByDay,
    discountImpact,
    reviews,
    isLoading,
    isError,
    isNotFound,
    isFetching:
      popularity.isFetching ||
      usageByDay.isFetching ||
      discountImpact.isFetching ||
      reviews.isFetching,
  };
}
